import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { minutesInMs } from '@gains-com/shared';
import { Platform } from './platform.service';
import { LOGGER_FACTORY } from './logger/logger-factory.provider';
import { AppUpdateComponent } from '../components/app-update/app-update.component';
import { environment } from '../../environments/environment.js';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerRegistrator {
  private readonly _platform = inject(Platform);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _logger = inject(LOGGER_FACTORY).create('ServiceWorkerService');

  async initialize() {
    if (this._platform.isServer || !('serviceWorker' in navigator)) {
      return Promise.resolve();
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        type: 'module',
        scope: '/',
      });

      // Listen for messages from the service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
          this._logger.log('Received message: New service worker installed; page needs to reload for latest content.');
          this._snackBar.openFromComponent(AppUpdateComponent, {
            panelClass: 'app-update-snackbar',
            duration: minutesInMs(10000),
          });
        } else if (event.data && event.data.type === 'SERVICE_WORKER_ACTIVATED') {
          // New SW is fully controlling the page. We might want to close any existing snackbar here if the user hasn't refreshed yet or ensure it only shows once.
        }
      });

      // Listen for updates to the service worker
      registration.addEventListener('updatefound', () => {
        this._logger.log('New service worker found.');
        const newWorker = registration.installing;

        newWorker?.addEventListener('statechange', () => {
          // Has service worker state changed?
          switch (newWorker.state) {
            case 'installed':
              if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                // A previous service worker is controlling the page and a new worker has been installed.
                this._logger.log('New service worker installed; page needs to reload for latest content.');
                // this._snackBar.openFromComponent(AppUpdateComponent, { duration: minutesInMs(10000) });
              } else {
                // Initial install
                this._logger.log('Service worker installed for the first time.');
              }
              break;
            case 'installing':
              this._logger.log('Service worker installing.');
              break;
            case 'activating':
              this._logger.log('Service worker activating.');
              break;
            case 'activated':
              this._logger.log('Service worker activated and controlling the page.');
              break;
          }
        });
      });

      // Periodically check for updates
      setInterval(() => {
        registration.update();
      }, minutesInMs(5)); // Check every 5 minutes
    } catch (err) {
      this._logger.error(`Failed to register service worker successfully: ${err}`);
    }
  }
}
